/* Home — the feed.
 *
 * Someone in no church still sees a full feed. That is the model: an account
 * stands on its own, and church content is additive.
 */
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PostSkeleton } from '@/components/ui/skeleton'
import { useFeed, useMe } from '@/api/queries'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { Composer } from './Composer'
import { CallsRow } from '@/features/church/UpcomingCalls'
import { ServiceBanner } from '@/features/attendance/HomeBanner'
import { QuestionsNudge } from '@/features/account/QuestionsNudge'
import { PostCard } from './PostCard'
import { useFocusTarget } from '@/features/notifications/useFocusTarget'
import { usePost } from '@/api/queries'
import type { PostType } from '@/types/api'

export function FeedPage({
  churchId,
  postType,
  empty,
}: {
  churchId?: string | null
  /** Narrows to one kind — 'testimony' is the Testimonies page. */
  postType?: PostType
  /** What to say when there is nothing. The home feed's wording is about
   *  people and churches, which reads as an error on a filtered page. */
  empty?: { title: string; body: string }
}) {
  const { data: me } = useMe()
  const feed = useFeed(churchId, postType)
  /* Which post and comment the bell sent us to, if any. */
  const focus = useFocusTarget('post')

  const sentinel = useInfiniteScroll(
    () => feed.fetchNextPage(),
    { enabled: feed.hasNextPage && !feed.isFetchingNextPage },
  )

  const loaded = feed.data?.pages.flatMap((p) => p.items) ?? []

  /* THE POST A NOTIFICATION POINTED AT, LIFTED TO THE TOP.
   *
   * Not a page of its own: one card on an empty background loses everything
   * that makes a feed a place, and is a dead end once it has been read. Not a
   * scroll-to-find either — the post can be two hundred down or not loaded at
   * all, so the page would sit there fetching while somebody waits.
   *
   * It rides at the top, ringed, and the ordinary feed continues underneath.
   * Refreshing drops the ?post= param and the feed is a feed again. */
  const pinned = usePost(focus.entityId)
  const posts =
    pinned.data && !loaded.some((p) => p.id === pinned.data!.id)
      ? [pinned.data, ...loaded]
      : pinned.data
        ? [pinned.data, ...loaded.filter((p) => p.id !== pinned.data!.id)]
        : loaded

  return (
    <div className="space-y-s4">
      {/* A service on right now, and this person not yet checked in. Home
          only — the one page everybody opens on arrival. */}
      {!churchId && !postType && me?.church_id ? (
        <ServiceBanner churchId={me.church_id} />
      ) : null}

      {/* Questions the church added since this person joined, or a required
          one still unanswered. Home only, for the same reason. */}
      {!churchId && !postType ? (
        <QuestionsNudge inChurch={Boolean(me?.church_id)} />
      ) : null}

      {/* No composer on a filtered page: a testimony is written from the
          Prayer Request it belongs to, not typed into a box here. */}
      {churchId || postType ? null : <Composer />}

      {/* Calls and lives this person is invited to, as a row of tall
          cards under the composer — what is on now marked LIVE — and the
          way to the whole list. Not on a filtered page, where the reader
          came for one kind of thing. See UpcomingCalls. */}
      <CallsRow enabled={!churchId && !postType} />

      {feed.isLoading ? (
        <>
          <PostSkeleton />
          <PostSkeleton />
          <PostSkeleton />
        </>
      ) : null}

      {feed.isError ? (
        <div className="cn-card p-s6 text-center">
          <p className="font-semibold">This feed did not load.</p>
          <p className="mt-1 text-base text-ink-2">
            {(feed.error as Error)?.message}
          </p>
          <Button variant="secondary" className="mt-s4" onClick={() => feed.refetch()}>
            Try again
          </Button>
        </div>
      ) : null}

      {!feed.isLoading && !feed.isError && posts.length === 0 ? (
        <div className="cn-card p-s8 text-center">
          <p className="text-md font-bold">
            {empty?.title ?? 'Nothing here yet'}
          </p>
          <p className="mt-1 text-base text-ink-2">
            {empty?.body ??
              'Posts from people you follow and from your church will appear here.'}
          </p>
        </div>
      ) : null}

      {posts.map((post) => (
        <PostCard
          key={post.id}
          post={post}
          viewerId={me?.id}
          focusComment={focus.entityId === post.id ? focus.commentId : null}
          highlighted={focus.entityId === post.id}
        />
      ))}

      {/* The scroll trigger. Sits below the last post so the next page starts
          loading before anyone reaches the end. */}
      <div ref={sentinel} className="h-px" aria-hidden />

      {feed.isFetchingNextPage ? (
        <div className="flex justify-center py-s4">
          <Loader2 className="h-5 w-5 animate-spin text-ink-3" aria-label="Loading more" />
        </div>
      ) : null}

      {!feed.hasNextPage && posts.length > 0 ? (
        <p className="py-s4 text-center text-sm font-semibold text-ink-2">
          You are all caught up.
        </p>
      ) : null}
    </div>
  )
}
